import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { availableSlots, inscriptionCategories, type ActivitySlot } from '../data/loisirs'
import type { ShellAtnaTurn, ShellTurn } from '../types/atna'

export type InscriptionKind = 'activite' | 'resultats' | 'confirmation'

const THINKING_MS = 700

/** Finds the category named in a free-form sentence (“a swim class”…). */
export function detectCategory(text: string): string | null {
  const lower = text.toLowerCase()
  const aliases: Record<string, string[]> = {
    Swimming: ['swim', 'swimming', 'pool', 'aquatic', 'lesson'],
    Soccer: ['soccer', 'ball', 'football'],
    Skating: ['skat', 'ice', 'hockey', 'rink'],
    'Visual arts': ['art', 'arts', 'drawing', 'painting', 'craft'],
    Music: ['music', 'guitar', 'piano', 'singing', 'choir'],
  }
  const match = inscriptionCategories.find((category) =>
    aliases[category]?.some((alias) => lower.includes(alias)),
  )
  return match ?? null
}

/**
 * ATNA registration flow: ATNA asks for the activity, looks up the open
 * sessions, then registers the child in one click.
 */
export function useInscriptionConversation(initialText: string) {
  const seed = initialText.trim()

  const [turns, setTurns] = useState<ShellTurn[]>(() => [{ id: 't0', role: 'user', text: seed }])
  const [thinking, setThinking] = useState(true)
  const [draft, setDraft] = useState('')
  const [category, setCategory] = useState<string | null>(() => detectCategory(seed))
  const [slot, setSlot] = useState<ActivitySlot | null>(null)

  const idRef = useRef(1)
  const timerRef = useRef<number | null>(null)

  const reply = useCallback((kind: InscriptionKind) => {
    if (timerRef.current) window.clearTimeout(timerRef.current)
    setThinking(true)
    timerRef.current = window.setTimeout(() => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'atna', kind }])
      setThinking(false)
    }, THINKING_MS)
  }, [])

  const exchange = useCallback(
    (userText: string, kind: InscriptionKind) => {
      idRef.current += 1
      setTurns((current) => [...current, { id: `t${idRef.current}`, role: 'user', text: userText }])
      reply(kind)
    },
    [reply],
  )

  useEffect(() => {
    // If the activity is already named in the request, ATNA goes straight to the open spots.
    reply(detectCategory(seed) ? 'resultats' : 'activite')
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const lastAtna = useMemo(
    () => [...turns].reverse().find((turn): turn is ShellAtnaTurn => turn.role === 'atna'),
    [turns],
  )

  const pickCategory = useCallback(
    (value: string) => {
      setCategory(value)
      setSlot(null)
      exchange(value, 'resultats')
    },
    [exchange],
  )

  const register = useCallback(
    (chosen: ActivitySlot) => {
      setSlot(chosen)
      exchange(`Register Emma — ${chosen.name}, ${chosen.day} ${chosen.time}`, 'confirmation')
    },
    [exchange],
  )

  const restart = useCallback(() => {
    setCategory(null)
    setSlot(null)
    exchange('Register for another activity', 'activite')
  }, [exchange])

  const submitDraft = useCallback(() => {
    const text = draft.trim()
    if (!text || thinking) return
    setDraft('')
    const detected = detectCategory(text)
    if (detected) {
      setCategory(detected)
      setSlot(null)
      exchange(text, 'resultats')
      return
    }
    exchange(text, lastAtna?.kind === 'activite' ? 'activite' : 'resultats')
  }, [draft, exchange, lastAtna, thinking])

  const slots = useMemo(
    () => (category ? availableSlots.filter((item) => item.category === category) : []),
    [category],
  )

  return {
    turns,
    thinking,
    draft,
    setDraft,
    submitDraft,
    category,
    slots,
    slot,
    pickCategory,
    register,
    restart,
    liveAtnaId: lastAtna?.id,
    lastAtnaKind: lastAtna?.kind as InscriptionKind | undefined,
  }
}
